import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, Tag } from "lucide-react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import SEO from "@/components/SEO";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const categories = Array.from(new Set(posts.map((p) => p.category))).sort();
  const filtered = posts.filter((p) =>
    (!cat || p.category === cat) &&
    (!q || p.title.toLowerCase().includes(q.toLowerCase()) || p.excerpt.toLowerCase().includes(q.toLowerCase()))
  );
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <SEO
        title="Knowledge Centre — West Bengal Real Estate, Surveys & Records"
        description="Articles on land mutation, Porcha / Record of Rights, LR and RS records, land conversion, digital land survey (RTK GNSS, DGPS) and property buyer guides for Kolkata and South 24 Parganas."
        path="/blog"
        keywords="land mutation West Bengal, porcha, record of rights, RTK GNSS, DGPS, land conversion, Dag Khatian"
      />
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-28 pb-14">
          <div className="ae-eyebrow mb-3">Knowledge Centre</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl">Articles on West Bengal real estate, surveying & records</h1>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container">
          <div className="ae-card p-4 mb-8 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3">
              <Search className="w-4 h-4 text-[#64748B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="w-full py-2.5 text-sm bg-transparent focus:outline-none" data-testid="blog-search" />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="blog-cat">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          {featured && (
            <Link to={`/blog/${featured.slug}`} className="ae-card overflow-hidden grid md:grid-cols-5 mb-10" data-testid="blog-featured">
              <div className="md:col-span-3 aspect-[16/10] md:aspect-auto bg-[#F4F6F8]">
                <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover" />
              </div>
              <div className="md:col-span-2 p-6 md:p-10 flex flex-col justify-center">
                <span className="ae-chip w-fit mb-3"><Tag className="w-3 h-3" /> {featured.category}</span>
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#101827] mb-3 leading-snug">{featured.title}</h2>
                <p className="text-[#64748B] leading-relaxed">{featured.excerpt}</p>
                <div className="mt-5 text-sm text-[#183B66] font-semibold">Read article →</div>
              </div>
            </Link>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="ae-card overflow-hidden" data-testid={`blog-card-${p.slug}`}>
                <div className="aspect-[4/3] bg-[#F4F6F8]"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <span className="ae-chip mb-3">{p.category}</span>
                  <h3 className="font-bold text-[#101827] text-lg leading-snug line-clamp-2 mb-2">{p.title}</h3>
                  <p className="text-sm text-[#64748B] line-clamp-3">{p.excerpt}</p>
                  <div className="mt-3 text-xs text-[#64748B] flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.author}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
